import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/UI/Button";
import { emailVerificationApiService } from "../../infrastructure/api/email-verification-api-service";
import { useAuthStore } from "../stores/auth-store";

export const EmailVerifiedPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshToken } = useAuthStore();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorReason, setErrorReason] = useState<string | null>(null);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setStatus("error");
        setErrorReason("유효하지 않은 링크입니다.");
        return;
      }

      try {
        const result = await emailVerificationApiService.verifyEmail(token);

        if (result.success) {
          setStatus("success");
          // 토큰 갱신하여 사용자 정보 업데이트
          try {
            await refreshToken();
          } catch (e) {
            // 토큰 갱신 실패 시 무시 (로그인 필요)
          }
        } else {
          setStatus("error");
          switch (result.reason) {
            case "INVALID_TOKEN":
            case "INVALID_TOKEN_TYPE":
              setErrorReason("유효하지 않은 인증 링크입니다.");
              break;
            case "ALREADY_USED":
              setErrorReason("이미 사용된 인증 링크입니다.");
              break;
            case "EXPIRED":
              setErrorReason("인증 링크가 만료되었습니다. 다시 발송해 주세요.");
              break;
            default:
              setErrorReason("인증 처리 중 오류가 발생했습니다.");
          }
        }
      } catch (err: any) {
        setStatus("error");
        setErrorReason(err.message || "인증 처리 중 오류가 발생했습니다.");
      }
    };

    verifyEmail();
  }, [searchParams, refreshToken]);

  const handleGoHome = () => {
    navigate("/", { replace: true });
  };

  const handleResend = () => {
    navigate("/auth/email-verification", { replace: true });
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#E8EFFF] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-[#4285F4] animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-[#222] mb-3">
              이메일 인증 중...
            </h2>
            <p className="text-[#555]">
              잠시만 기다려 주세요.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#FEF2F2] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-[#F24A4A]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-[#222] mb-3">
              인증 실패
            </h2>
            <p className="text-[#555] mb-6">
              {errorReason}
            </p>
            <div className="space-y-3">
              <Button
                type="button"
                onClick={handleResend}
                className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white font-semibold"
                style={{ height: "48px", borderRadius: "8px" }}
              >
                인증 이메일 다시 받기
              </Button>
              <Button
                type="button"
                onClick={handleGoHome}
                className="w-full bg-white text-[#222] border border-[#222] hover:bg-[#f5f5f5]"
                style={{ height: "48px", borderRadius: "8px" }}
              >
                메인으로 돌아가기
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // success
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#E8EFFF] rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-[#4285F4]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-[#222] mb-3">
            이메일 인증 완료
          </h2>
          <p className="text-[#555] mb-6">
            이메일 인증이 완료되었습니다.
            <br />
            이제 모든 서비스를 이용할 수 있습니다.
          </p>
          <Button
            type="button"
            onClick={handleGoHome}
            className="w-full bg-[#222] text-white hover:bg-[#333] font-semibold"
            style={{ height: "48px", borderRadius: "8px" }}
          >
            시작하기
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerifiedPage;
